import React, {useMemo, useRef, useState} from 'react';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IDetailsListProps,
    Icon,
    Link,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import PanelWrapper from '../Common/PanelWrapper';
import Badge from '../Common/Badge';
import CurrencyDisplay from '../Currency/CurrencyDisplay';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection, {MARKUP_TYPES} from './MarkupTypeSelection';
import SuggestionReasonChip from './SuggestionReasonChip';
import {useGetStorefrontProductSuggestionsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {IStorefrontSuggestedProduct} from '../../models/Storefront';
import {
    ISuggestionListingEntry,
    suggestionAmountState,
    suggestionDetail,
    suggestionReason,
    SUGGESTION_PANEL_SIZE,
    SUGGESTION_SOURCES,
} from './suggestedProductsLogic';

interface ISuggestedProductsPanelProps {
    storefront: string;
    /** Products the strip already knows landed on the store, from this panel or from a card. */
    addedIds: string[];
    /** An add is in flight somewhere, so nothing here should start another one. */
    busy: boolean;
    /** Opens the strip's single shared details modal on this product. */
    onOpenDetails: (productId: string, catalog: string) => void;
    /**
     * Lists the chosen products in one call and resolves with the ids that actually landed. The strip owns
     * this so the toast, the added bookkeeping and the grid refresh stay in one place.
     */
    onAddSelected: (entries: ISuggestionListingEntry[], catalog?: string) => Promise<string[]>;
    onDismiss: () => void;
}

/** What the user typed against one row before listing it. Absent fields mean "inherit". */
interface IRowMarkup {
    markup?: number;
    markupType?: string;
}

/** The friendly name for a markup code, for saying what a row would inherit if left alone. */
const markupTypeName = (code?: string): string => {
    const names = MARKUP_TYPES as {[key: string]: string | undefined};
    if (!code) return 'Percentage';
    return names[code] ?? code;
};

/**
 * A cell that holds something clickable.
 *
 * DetailsList wraps its rows in a SelectionZone, which reads clicks and key presses anywhere in the row as
 * "select this row". A link would therefore toggle the checkbox on its way to opening the modal, and typing a
 * space into the markup field would toggle it too. `data-selection-disabled` is the opt-out Fluent honours,
 * and the stopped handlers cover the paths it does not.
 */
const InteractiveCell: React.FC<{title?: string}> = ({title, children}) => (
    <div
        data-selection-disabled='true'
        title={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
    >
        {children}
    </div>
);

/**
 * The full suggestion list, with the pricing each product would carry and the markup to give it.
 *
 * Its own read of the suggestions, because the strip deliberately asks for only a handful. Everything about
 * what has been added and what is in flight belongs to the strip and arrives as props, so a product listed
 * from in here also leaves the strip behind the panel.
 */
const SuggestedProductsPanel: React.FC<ISuggestedProductsPanelProps> = (props) => {
    const theme = useTheme();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [markups, setMarkups] = useState<{[productId: string]: IRowMarkup}>({});

    // The panel mounts only when opened, and excludeListed makes the answer change as products land on the
    // store, so a cached copy from the previous open is exactly what we do not want.
    const suggestions = useGetStorefrontProductSuggestionsQuery(
        {
            storefront: props.storefront,
            size: SUGGESTION_PANEL_SIZE,
            excludeListed: true,
            sources: SUGGESTION_SOURCES,
            // Out of stock products are worth listing here: a shopper seeing them is how a backorder starts.
            includeOutOfStock: true,
            // bundleSize 0 asks the API to skip the bundle passes: this panel only reads products.
            bundleSize: 0,
        },
        {refetchOnMountOrArgChange: true}
    );
    const products = useMemo(() => suggestions.data?.products ?? [], [suggestions.data]);
    const catalog = suggestions.data?.catalog;
    const defaultMarkup = suggestions.data?.defaultMarkup;
    const defaultMarkupType = suggestions.data?.defaultMarkupType;

    // Fluent reads the selection prop in its constructor and never again, so this is built once and handed
    // over from the very first render. getKey keyed on the product id is what lets a selection survive the
    // items array being rebuilt underneath it.
    const selectionRef = useRef<Selection>();
    if (!selectionRef.current) {
        const created: Selection = new Selection({
            getKey: (item) => (item as IStorefrontSuggestedProduct).id,
            onSelectionChanged: () => {
                setSelectedIds((created.getSelection() as IStorefrontSuggestedProduct[]).map((p) => p.id));
            },
        });
        selectionRef.current = created;
    }
    const selection = selectionRef.current;

    const setRowMarkup = (productId: string, change: IRowMarkup) => {
        setMarkups((current) => ({...current, [productId]: {...current[productId], ...change}}));
    };

    // Already-listed products stay in the table with their controls swapped out, so a re-tick of one of them
    // must not be counted or sent.
    const addableIds = selectedIds.filter((id) => !props.addedIds.includes(id));

    const addSelected = async () => {
        if (addableIds.length === 0 || props.busy) return;
        const entries: ISuggestionListingEntry[] = addableIds.map((id) => ({
            id,
            markup: markups[id]?.markup,
            markupType: markups[id]?.markupType,
        }));
        const landed = await props.onAddSelected(entries, catalog);
        if (landed.length === 0) return;
        // Only what landed is cleared. Anything the server skipped stays ticked and keeps its markup, so a
        // second attempt does not mean typing it all again.
        landed.forEach((id) => selection.setKeySelected(id, false, false));
        setMarkups((current) => {
            const next = {...current};
            landed.forEach((id) => delete next[id]);
            return next;
        });
    };

    // What a row would inherit if its markup is left alone, shown in the field and spelled out on hover.
    const markupPlaceholder = `${defaultMarkup ?? 'Not set'} (inherited)`;
    const inheritedLabel = `${defaultMarkup ?? 'Not set'} ${markupTypeName(defaultMarkupType)}`;

    const columns: IColumn[] = [
        {
            key: 'thumbnail',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (product: IStorefrontSuggestedProduct) => product.imageUrl ? (
                <img
                    src={product.imageUrl}
                    alt=''
                    style={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        border: `1px solid ${theme.palette.neutralLight}`,
                        borderRadius: 2,
                    }}
                />
            ) : null,
        },
        {
            key: 'product',
            name: 'Product',
            minWidth: 220,
            isMultiline: true,
            onRender: (product: IStorefrontSuggestedProduct) => {
                const detail = suggestionDetail(product);
                return (
                    <Stack styles={{root: {minWidth: 0}}}>
                        <InteractiveCell title={product.name}>
                            <Link
                                onClick={() => catalog && props.onOpenDetails(product.id, catalog)}
                                disabled={!catalog}
                            >
                                {product.name ?? 'Untitled'}
                            </Link>
                        </InteractiveCell>
                        {detail && (
                            <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                                {detail}
                            </Text>
                        )}
                    </Stack>
                );
            },
        },
        {
            // A column of its own rather than another line under the product name: the reasons line up and can
            // be read down the table, which is the point of showing them at all in a mixed list.
            key: 'reason',
            name: 'Suggested Because',
            minWidth: 120,
            maxWidth: 150,
            onRender: (product: IStorefrontSuggestedProduct) => (
                <SuggestionReasonChip reason={suggestionReason(product)}/>
            ),
        },
        {
            key: 'stock',
            name: 'Stock',
            minWidth: 90,
            maxWidth: 100,
            onRender: (product: IStorefrontSuggestedProduct) => (
                <Badge
                    text={product.inStock ? 'In Stock' : 'Out of Stock'}
                    color={product.inStock ? 'success' : 'error'}
                />
            ),
        },
        {
            key: 'cost',
            name: 'Cost',
            minWidth: 100,
            maxWidth: 120,
            isMultiline: true,
            onRender: (product: IStorefrontSuggestedProduct) => {
                const state = suggestionAmountState(product.cost, product.hidePrice);
                if (state !== 'value') {
                    return (
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                            {state === 'hidden' ? 'Hidden' : 'No cost'}
                        </Text>
                    );
                }
                return (
                    <Stack>
                        <Text variant='medium'><CurrencyDisplay value={product.cost}/></Text>
                        {product.costVendor && (
                            <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                                {product.costVendor}
                            </Text>
                        )}
                    </Stack>
                );
            },
        },
        {
            key: 'price',
            name: 'Price',
            minWidth: 90,
            maxWidth: 110,
            onRender: (product: IStorefrontSuggestedProduct) => {
                const state = suggestionAmountState(product.price, product.hidePrice);
                if (state !== 'value') {
                    return (
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                            {state === 'hidden' ? 'Hidden' : 'No price'}
                        </Text>
                    );
                }
                return <Text variant='medium'><CurrencyDisplay value={product.price}/></Text>;
            },
        },
        {
            key: 'markup',
            name: 'Markup Override',
            minWidth: 260,
            isMultiline: true,
            onRender: (product: IStorefrontSuggestedProduct) => {
                if (props.addedIds.includes(product.id)) {
                    return (
                        <Text variant='small' styles={{root: {color: theme.palette.themePrimary}}}>
                            <Icon iconName='CheckMark' styles={{root: {marginRight: 4}}}/>
                            Added
                        </Text>
                    );
                }
                const row = markups[product.id] ?? {};
                return (
                    <InteractiveCell title={`Leave blank to inherit ${inheritedLabel}`}>
                        <Stack horizontal tokens={{childrenGap: '0.5em'}} verticalAlign='center'>
                            <Stack.Item styles={{root: {width: 110}}}>
                                <NumberField
                                    value={row.markup}
                                    decimal
                                    placeholder={markupPlaceholder}
                                    onChange={(value) => setRowMarkup(product.id, {markup: value})}
                                />
                            </Stack.Item>
                            <Stack.Item styles={{root: {width: 130}}}>
                                <MarkupTypeSelection
                                    value={row.markupType ?? ''}
                                    onChange={(markupType) => setRowMarkup(product.id, {markupType})}
                                />
                            </Stack.Item>
                        </Stack>
                    </InteractiveCell>
                );
            },
        },
    ];

    // Two lines of text and a pair of form controls do not fit the default row height.
    const onRenderRow: IDetailsListProps['onRenderRow'] = (rowProps, defaultRender) => {
        if (!rowProps || !defaultRender) return null;
        return defaultRender({...rowProps, styles: {root: {minHeight: 72}, fields: {alignItems: 'center'}}});
    };

    const footerButtons = [
        <PrimaryButton
            key='add'
            text={props.busy ? 'Adding...' : `Add Selected (${addableIds.length})`}
            iconProps={props.busy ? undefined : {iconName: 'Add'}}
            disabled={addableIds.length === 0 || props.busy || !catalog}
            onClick={addSelected}
        />,
        <DefaultButton key='close' text='Close' onClick={props.onDismiss}/>,
    ];

    return (
        <PanelWrapper
            isOpen={true}
            onDismiss={props.onDismiss}
            title='Suggested Products'
            type={PanelType.large}
            isFooterAtBottom
            footerButtons={footerButtons}
        >
            <Stack tokens={{childrenGap: '1em'}} styles={{root: {padding: '1em 0'}}}>
                <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                    Products this store is likely missing. Tick what belongs on it, set a markup on anything
                    that needs one, then add them together.
                </Text>

                {suggestions.isLoading && <Spinner size={SpinnerSize.medium} label='Finding products...'/>}

                {!suggestions.isLoading && suggestions.isError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        We could not load suggestions right now.
                    </MessageBar>
                )}

                {!suggestions.isLoading && !suggestions.isError && products.length === 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        There is nothing left to suggest. Everything we found is already on this store.
                    </MessageBar>
                )}

                {products.length > 0 && (
                    <DetailsList
                        items={products}
                        columns={columns}
                        selection={selection}
                        selectionMode={SelectionMode.multiple}
                        selectionPreservedOnEmptyClick
                        layoutMode={DetailsListLayoutMode.justified}
                        setKey='suggestedProducts'
                        getKey={(item: IStorefrontSuggestedProduct) => item.id}
                        onRenderRow={onRenderRow}
                    />
                )}
            </Stack>
        </PanelWrapper>
    );
};

export default SuggestedProductsPanel;
